import { useCallback, useState } from "react";
import { FormProvider, SubmitHandler } from "react-hook-form";

import DocumentationIconButton from "components/DocumentationIconButton";
import Markdown from "components/markdown/Markdown";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import CardCollapsible from "ds/components/Card/Collapsible";
import { showFullDescription } from "ds/components/FullDescriptionDrawer";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Typography from "ds/components/Typography";
import { useFormValidations } from "hooks/useFormValidations";
import { Blueprint, BlueprintDeployment, BlueprintState } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";

import DeploymentForm from "../components/DeploymentForm";
import { VersionTemplateFormFields } from "../components/DeploymentForm/types";
import useDeploymentForm from "../components/DeploymentForm/useDeploymentForm";
import styles from "./styles.module.css";

type UpdateDeploymentFormProps = {
  version: Blueprint;
  deployment: BlueprintDeployment;
  templateId: string;
  onClose: () => void;
};

const UpdateDeploymentForm = ({
  version,
  deployment,
  templateId,
  onClose,
}: UpdateDeploymentFormProps) => {
  const [isInstructionsCollapsed, setIsInstructionsCollapsed] = useState(false);

  const { builderForm, onSubmit, loading, hasErrors, hasPreview, errors, inputs } =
    useDeploymentForm({
      version,
      deployment,
      templateId,
      submitCallback: onClose,
    });

  const handleOpenFullDescription = useCallback(() => {
    if (version.description) {
      showFullDescription({ description: version.description });
    }
  }, [version.description]);

  const {
    handleSubmit,
    formState: { isDirty },
    register,
    watch,
  } = builderForm;
  const runFormValidations = useFormValidations(builderForm);

  const handleUpdate: SubmitHandler<VersionTemplateFormFields> = useCallback(
    async (formData) => {
      const isValid = await runFormValidations();
      if (!isValid) return;

      onSubmit(formData);
    },
    [onSubmit, runFormValidations]
  );

  const deploymentInstructions = version.instructions;

  return (
    <FormProvider {...builderForm}>
      <form onSubmit={handleSubmit(handleUpdate)}>
        <FullScreenModalBody noMaxWidth>
          <Box justify="center" gap="x-large" className={styles.contentWrapper} fullWidth>
            <Box
              direction="column"
              shrink="0"
              padding="x-large"
              align="center"
              className={styles.formSection}
            >
              <Box direction="column" className={styles.formFields}>
                <DeploymentForm
                  item={version}
                  isPublished={version.state === BlueprintState.Published}
                  hasPreview={hasPreview}
                  hasErrors={hasErrors}
                  errors={errors}
                  inputs={inputs}
                  register={register}
                  watch={watch}
                  isUpdateMode
                  onOpenFullDescription={handleOpenFullDescription}
                />
              </Box>
            </Box>

            <Box
              direction="column"
              shrink="0"
              padding="x-large"
              className={styles.instructionsSection}
            >
              <CardCollapsible
                title="Instructions"
                isCollapsed={isInstructionsCollapsed}
                onToggle={() => setIsInstructionsCollapsed(!isInstructionsCollapsed)}
                ariaLevel={2}
                ariaLabel="Deployment instructions"
              >
                {deploymentInstructions ? (
                  <Markdown markup={deploymentInstructions} />
                ) : (
                  <Typography tag="div" variant="p-body2" color="secondary">
                    No deployment instructions provided for this template.
                  </Typography>
                )}
              </CardCollapsible>
            </Box>
          </Box>
        </FullScreenModalBody>

        <FullScreenModalFooter justify="between">
          <DocumentationIconButton
            href={getDocsUrl("/concepts/template/deployments")}
            tooltipText="Go to templates documentation"
            size="small"
          />
          <Button variant="primary" type="submit" loading={loading} disabled={loading || !isDirty}>
            Update
          </Button>
        </FullScreenModalFooter>
      </form>
    </FormProvider>
  );
};

export default UpdateDeploymentForm;
